import { useState } from 'react';
import { Activity, Lock, AlertCircle } from 'lucide-react';

export default function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Login failed');
        return;
      }

      localStorage.setItem('korinth_token', data.token);
      onLogin(data);
    } catch {
      setError('Cannot connect to server');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-korinth-bg flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Activity className="w-8 h-8 text-korinth-accent" />
            <span className="text-2xl font-bold text-white font-mono tracking-wider">KORINTH</span>
          </div>
          <p className="text-xs text-korinth-muted">Strategic Intelligence Operating System</p>
        </div>

        {/* Login form */}
        <form onSubmit={handleSubmit} className="bg-korinth-surface border border-korinth-border rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-4 h-4 text-korinth-muted" />
            <span className="text-sm font-mono text-korinth-text">Authentication Required</span>
          </div>

          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-korinth-danger/10 border border-korinth-danger/30 rounded text-xs text-korinth-danger font-mono">
              <AlertCircle className="w-3 h-3 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider block mb-1">Username</label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoFocus
              autoComplete="username"
              className="w-full px-3 py-2.5 bg-korinth-bg border border-korinth-border rounded text-sm font-mono text-white placeholder-korinth-muted/50 focus:border-korinth-accent focus:outline-none"
              placeholder="Username"
            />
          </div>

          <div>
            <label className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider block mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full px-3 py-2.5 bg-korinth-bg border border-korinth-border rounded text-sm font-mono text-white placeholder-korinth-muted/50 focus:border-korinth-accent focus:outline-none"
              placeholder="Enter password"
            />
          </div>

          <button
            type="submit"
            disabled={loading || !username || !password}
            className="w-full py-2.5 rounded bg-korinth-accent text-korinth-bg text-sm font-mono font-bold hover:bg-korinth-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Authenticating...' : 'Login'}
          </button>
        </form>

        <p className="text-center text-[10px] text-korinth-muted/40 font-mono mt-4">
          KORINTH v0.3 — Owner Access Only
        </p>
      </div>
    </div>
  );
}
